import { fillTemplate, type Template, type TemplateVariable } from "@wcp/shared";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { ApiError } from "../../lib/api.js";
import { useBusiness } from "../../lib/business.js";
import { uploadMediaFile } from "../../lib/upload.js";
import { useMe } from "../auth/api.js";
import { useAudiences } from "../contacts/api.js";
import { categoryLabel, money, useCosts } from "../costs/api.js";
import { useTemplates } from "../templates/api.js";
import {
  type BroadcastRow,
  useBroadcast,
  useBroadcasts,
  useCreateBroadcast,
  useSendBroadcast,
} from "./api.js";
import styles from "./BroadcastsScreen.module.css";

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  sending: "Sending",
  sent: "Sent",
  paused: "Paused",
  cancelled: "Cancelled",
};

export function BroadcastsScreen() {
  const { activeId } = useBusiness();
  const me = useMe();
  const broadcasts = useBroadcasts(activeId);
  const canSend = me.data?.role === "admin";
  const [composeOpen, setComposeOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [costDays, setCostDays] = useState(30);
  const costs = useCosts(activeId, costDays);
  const wa = costs.data?.whatsapp;

  const list = broadcasts.data ?? [];
  const stats = useMemo(() => {
    // The band says "last 30 days" — filter to it (the API returns all history).
    const cutoff = Date.now() - 30 * 86_400_000;
    const sentList = list.filter(
      (b) => b.status === "sent" && Date.parse(b.startedAt ?? b.createdAt) >= cutoff,
    );
    const messagesSent = sentList.reduce((n, b) => n + b.sentCount, 0);
    // Recipient-weighted delivered rate — a 1-recipient test shouldn't count as
    // much as a 10k send.
    const deliveredTotal = sentList.reduce((n, b) => n + b.deliveredCount, 0);
    const avgDelivered = messagesSent > 0 ? (deliveredTotal / messagesSent) * 100 : 0;
    // Soonest by schedule time, not most recently created.
    const nextScheduled =
      list
        .filter((b) => b.status === "scheduled" && b.scheduleAt)
        .sort(
          (a, b) => Date.parse(a.scheduleAt as string) - Date.parse(b.scheduleAt as string),
        )[0] ?? null;
    return { sentCount: sentList.length, messagesSent, avgDelivered, nextScheduled };
  }, [list]);

  return (
    <section className={styles.wrap}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Broadcasts</h1>
          <p className={styles.sub}>
            Bulk sends via approved templates · paced in batches to protect quality
          </p>
        </div>
        {canSend && (
          <button type="button" className="btn btn--primary" onClick={() => setComposeOpen(true)}>
            + New broadcast
          </button>
        )}
      </header>

      <div className={styles.miniBand}>
        <div className={styles.miniStat}>
          <span className={styles.msK}>Messages sent</span>
          <span className={`${styles.msV} ${styles.tnum}`}>
            {stats.messagesSent.toLocaleString()}
          </span>
          <span className={styles.msS}>last 30 days · {stats.sentCount} broadcasts</span>
        </div>
        <div className={styles.miniStat}>
          <span className={styles.msK}>Avg delivered</span>
          <span className={`${styles.msV} ${styles.tnum} ${styles.good}`}>
            {stats.avgDelivered.toFixed(1)}%
          </span>
          <span className={styles.msS}>across sent broadcasts</span>
        </div>
        <div className={styles.miniStat}>
          <span className={styles.msK}>Next scheduled</span>
          <span className={styles.msV}>
            {/* recipientsResolved only exists after send — show the audience until then */}
            {stats.nextScheduled ? stats.nextScheduled.audienceLabel : "None"}
          </span>
          <span className={styles.msS}>
            {stats.nextScheduled
              ? new Date(
                  stats.nextScheduled.scheduleAt ?? stats.nextScheduled.createdAt,
                ).toLocaleString()
              : "nothing queued"}
          </span>
        </div>
      </div>

      <div className={`${styles.panel} ${styles.costsPanel}`}>
        <div className={styles.costsHead}>
          <div className={styles.costsTitleWrap}>
            <span className={styles.costsTitle}>Messaging costs</span>
            <span className={styles.costsMeta}>
              charged by Meta per billed message
              {wa?.currency ? ` · billed in ${wa.currency}` : ""}
            </span>
          </div>
          {/* biome-ignore lint/a11y/useSemanticElements: a fieldset would need its browser chrome reset; this is a plain chip row */}
          <div className={styles.rangeChips} role="group" aria-label="Cost range">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                className={d === costDays ? styles.chipOn : styles.chip}
                onClick={() => setCostDays(d)}
              >
                {d} days
              </button>
            ))}
          </div>
        </div>
        {wa?.available ? (
          <div className={styles.costsBody}>
            <div className={styles.costsTiles}>
              <div className={styles.miniStat}>
                <span className={styles.msK}>Total charges</span>
                <span className={`${styles.msV} ${styles.tnum}`}>
                  {money(wa.total, wa.currency)}
                </span>
                <span className={styles.msS}>last {costDays} days</span>
              </div>
              <div className={styles.miniStat}>
                <span className={styles.msK}>Billed messages</span>
                <span className={`${styles.msV} ${styles.tnum}`}>{wa.volume.toLocaleString()}</span>
                <span className={styles.msS}>service replies in the 24-h window are free</span>
              </div>
            </div>
            {wa.byCategory.length > 0 ? (
              <div className={styles.costsTableWrap}>
                <table className={styles.costsTable}>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th className={styles.num}>Messages</th>
                      <th className={styles.num}>Billed</th>
                      <th className={styles.num}>Free</th>
                      <th className={styles.num}>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wa.byCategory.map((c) => (
                      <tr key={c.category}>
                        <td>{categoryLabel(c.category)}</td>
                        <td className={`${styles.num} ${styles.tnum}`}>
                          {c.volume.toLocaleString()}
                        </td>
                        <td className={`${styles.num} ${styles.tnum}`}>
                          {c.billed > 0 ? c.billed.toLocaleString() : "—"}
                        </td>
                        <td className={`${styles.num} ${styles.tnum}`}>
                          {c.free > 0 ? c.free.toLocaleString() : "—"}
                        </td>
                        <td className={`${styles.num} ${styles.tnum}`}>
                          {money(c.cost, wa.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className={styles.costsEmpty}>No billed messages in this range.</p>
            )}
          </div>
        ) : (
          <p className={styles.costsEmpty}>
            {costs.isLoading
              ? "Loading…"
              : "Billing syncs from Meta once this number is connected."}
          </p>
        )}
      </div>

      <div className={styles.panel}>
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Audience</th>
                <th className={styles.num}>Recipients</th>
                <th>When</th>
                <th className={styles.num}>Delivered</th>
                <th>Status</th>
                <th aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr>
                  <td className={styles.emptyRow} colSpan={7}>
                    No broadcasts yet.
                  </td>
                </tr>
              ) : (
                list.map((b) => {
                  const deliveredPct =
                    b.sentCount > 0
                      ? Math.round((b.deliveredCount / b.sentCount) * 1000) / 10
                      : null;
                  return (
                    <tr
                      key={b.id}
                      className={styles.rowClick}
                      tabIndex={0}
                      onClick={() => setDrawerId(b.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setDrawerId(b.id);
                        }
                      }}
                    >
                      <td className={styles.strong}>{b.name}</td>
                      <td>{b.audienceLabel}</td>
                      <td className={`${styles.mono} ${styles.num}`}>
                        {/* resolved at send time — draft/scheduled rows honestly show "—" */}
                        {b.recipientsResolved != null ? b.recipientsResolved.toLocaleString() : "—"}
                      </td>
                      <td className={`${styles.mono} ${styles.dim}`}>
                        {new Date(b.scheduleAt ?? b.createdAt).toLocaleString()}
                      </td>
                      <td className={`${styles.mono} ${styles.num}`}>
                        {deliveredPct != null ? `${deliveredPct}%` : "—"}
                      </td>
                      <td>
                        <StatusPill b={b} />
                      </td>
                      <td className={styles.rowGo} aria-hidden="true">
                        ›
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {composeOpen && <ComposeModal businessId={activeId} onClose={() => setComposeOpen(false)} />}
      {drawerId !== null && (
        <DetailDrawer id={drawerId} canSend={canSend} onClose={() => setDrawerId(null)} />
      )}
    </section>
  );
}

function StatusPill({ b }: { b: BroadcastRow }) {
  const cls =
    b.status === "sent"
      ? styles.ok
      : b.status === "paused"
        ? styles.warn
        : b.status === "sending"
          ? styles.live
          : styles.neutral;
  return (
    <span className={`${styles.pill} ${cls}`}>
      {STATUS_LABEL[b.status] ?? b.status}
      {b.status === "paused" && b.pauseReason && ` · ${b.pauseReason}`}
    </span>
  );
}

function DetailDrawer({
  id,
  canSend,
  onClose,
}: {
  id: number;
  canSend: boolean;
  onClose: () => void;
}) {
  const { activeId } = useBusiness();
  const detail = useBroadcast(id);
  const send = useSendBroadcast(activeId);
  const b = detail.data?.broadcast;
  const recips = detail.data?.recipients ?? [];

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close
    <div className={styles.scrim} onMouseDown={onClose}>
      <aside
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {!b ? (
          <p className={styles.empty}>Loading…</p>
        ) : (
          <>
            <header className={styles.drawerHead}>
              <h2>{b.name}</h2>
              <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
                ×
              </button>
            </header>
            <dl className={styles.dl}>
              <dt>Audience</dt>
              <dd>{b.audienceLabel}</dd>
              <dt>Status</dt>
              <dd>
                <StatusPill b={b} />
              </dd>
              <dt>Resolved</dt>
              <dd>{b.recipientsResolved ?? "—"}</dd>
              <dt>Sent / delivered / read</dt>
              <dd>
                {b.sentCount} / {b.deliveredCount} / {b.readCount}
              </dd>
              <dt>Failed</dt>
              <dd>{b.failedCount}</dd>
            </dl>
            {canSend && (b.status === "draft" || b.status === "scheduled") && (
              <button
                type="button"
                className="btn btn--primary"
                disabled={send.isPending}
                onClick={() => send.mutate(id)}
              >
                {send.isPending ? "Sending…" : "Send now"}
              </button>
            )}
            {send.data && (
              <p className={styles.sendResult}>
                Sent {send.data.sent} of {send.data.resolved} · {send.data.status}
              </p>
            )}
            <h3 className={styles.recipHead}>Recipients ({recips.length})</h3>
            <ul className={styles.recips}>
              {recips.slice(0, 100).map((r) => (
                <li key={r.id}>
                  <span>{r.phone}</span>
                  <span className={styles.recipStatus}>{r.status}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </aside>
    </div>
  );
}

function ComposeModal({ businessId, onClose }: { businessId: string; onClose: () => void }) {
  const templates = useTemplates();
  const audiences = useAudiences(businessId);
  const create = useCreateBroadcast(businessId);
  const send = useSendBroadcast(businessId);
  const [templateId, setTemplateId] = useState<number | null>(null);
  // Start with NO audience pre-selected so a careless click can't blast everyone.
  // A useEffect below defaults it to the "test-audience" safety list when present.
  const [audienceId, setAudienceId] = useState<string>("");
  const [vars, setVars] = useState<Record<string, string>>({});
  const [headerFile, setHeaderFile] = useState<File | null>(null);
  const [mode, setMode] = useState<"now" | "schedule">("now");
  const [scheduleAt, setScheduleAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const approved = (templates.data ?? []).filter((t: Template) => t.status === "approved");
  const tpl = approved.find((t) => t.id === templateId) ?? null;
  const tplVars = (tpl?.variables ?? []) as TemplateVariable[];
  const needsImage = tpl?.headerFormat === "image";
  // Template's saved default image (0018): broadcasts fall back to it server-side;
  // picking a file here overrides it for THIS broadcast only.
  const defaultHeaderUrl =
    tpl?.defaultHeaderMediaId != null ? `/api/v1/media/${tpl.defaultHeaderMediaId}` : null;
  const auds = audiences.data ?? [];
  const aud = auds.find((a) => String(a.id) === audienceId);
  const recipientCount = audienceId === "all" ? null : (aud?.count ?? null);

  const headerUrl = useMemo(
    () => (headerFile ? URL.createObjectURL(headerFile) : null),
    [headerFile],
  );
  useEffect(() => () => void (headerUrl && URL.revokeObjectURL(headerUrl)), [headerUrl]);

  // Safety default: preselect the "test-audience" list if it exists, so the composer
  // never opens pre-aimed at everyone. Only sets while the field is still untouched,
  // so it never overrides a deliberate choice. On envs without a test-audience the
  // field stays unselected and the user must pick explicitly (submit() enforces it).
  const testAudienceId = auds.find((a) => a.label === "test-audience")?.id;
  useEffect(() => {
    if (audienceId === "" && testAudienceId != null) setAudienceId(String(testAudienceId));
  }, [audienceId, testAudienceId]);

  const previewText = tpl
    ? fillTemplate(
        tpl.body,
        tplVars.map((v) => ({ ...v, label: vars[String(v.index)] || v.label })),
      )
    : null;

  function pickImage(file: File | null) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("The header must be an image (JPEG or PNG).");
      return;
    }
    setError(null);
    setHeaderFile(file);
  }

  const submit = async () => {
    setError(null);
    if (!tpl) {
      setError("Choose a template.");
      return;
    }
    if (needsImage && !headerFile && !defaultHeaderUrl) {
      setError("This template has an image header — add the image to send.");
      return;
    }
    if (audienceId === "") {
      setError("Choose an audience.");
      return;
    }
    if (mode === "schedule" && !scheduleAt) {
      setError("Pick a date and time.");
      return;
    }
    setBusy(true);
    try {
      let headerMediaId: number | undefined;
      if (needsImage && headerFile) {
        headerMediaId = (await uploadMediaFile(businessId, headerFile)).id;
      }
      const body = {
        templateId: tpl.id,
        audienceLabel: aud?.label ?? "All opted-in",
        ...(aud ? { audienceId: aud.id } : { audienceDefinition: {} }),
        ...(tplVars.length ? { templateVariables: vars } : {}),
        ...(headerMediaId !== undefined ? { headerMediaId } : {}),
        ...(mode === "schedule" ? { scheduleAt: new Date(scheduleAt).toISOString() } : {}),
      };
      const res = await create.mutateAsync(body);
      if (mode === "now") await send.mutateAsync(res.id);
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not create broadcast.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close
    <div className={styles.scrim} onMouseDown={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className={styles.drawerHead}>
          <h2>New broadcast</h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        {error && <div className={styles.error}>{error}</div>}

        <label className={styles.field}>
          <span>Template (approved only)</span>
          <select
            value={templateId ?? ""}
            onChange={(e) => {
              setTemplateId(Number(e.target.value) || null);
              setVars({});
              setHeaderFile(null);
            }}
          >
            <option value="">Select a template…</option>
            {approved.map((t) => (
              <option key={t.id} value={t.id}>
                {t.displayName ?? t.name} · {t.category}
                {t.headerFormat === "image" ? " · 🖼 image" : ""}
              </option>
            ))}
          </select>
        </label>

        {needsImage && (
          <div className={styles.field}>
            <span>Header image (sent to every recipient)</span>
            <label
              className={styles.imagePick}
              data-filled={Boolean(headerFile || defaultHeaderUrl)}
            >
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="sr-only"
                onChange={(e) => pickImage(e.target.files?.[0] ?? null)}
              />
              {headerFile ? (
                <>
                  <img src={headerUrl ?? undefined} alt="" />
                  <span>{headerFile.name} — tap to replace</span>
                </>
              ) : defaultHeaderUrl ? (
                <>
                  <img src={defaultHeaderUrl} alt="" />
                  <span>Template's saved image — tap to replace for this broadcast</span>
                </>
              ) : (
                <span>
                  <b>Choose an image</b> (JPEG/PNG, ≤5MB)
                </span>
              )}
            </label>
          </div>
        )}

        <label className={styles.field}>
          <span>Audience</span>
          <select value={audienceId} onChange={(e) => setAudienceId(e.target.value)}>
            <option value="">Select an audience…</option>
            <option value="all">All opted-in</option>
            {auds.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label} ({a.count})
              </option>
            ))}
          </select>
          <small className={styles.fieldHint}>
            {audienceId === "all" ? (
              "Every opted-in contact of this brand."
            ) : recipientCount != null ? (
              `≈ ${recipientCount} recipient${recipientCount === 1 ? "" : "s"} (resolved fresh at send time; opt-outs excluded).`
            ) : (
              <>
                Need a new list? <Link to="/contacts">Create an audience in Contacts →</Link>
              </>
            )}
          </small>
        </label>

        {tplVars.map((v) => (
          <label key={v.index} className={styles.field}>
            <span>
              Variable {`{{${v.index}}}`} — {v.label}
            </span>
            <input
              value={vars[String(v.index)] ?? ""}
              onChange={(e) => setVars((p) => ({ ...p, [String(v.index)]: e.target.value }))}
            />
          </label>
        ))}

        {tpl && (
          <div className={styles.field}>
            <span>Preview</span>
            <div className={styles.waPreview}>
              <div className={styles.waBubble}>
                {needsImage && (
                  <span className={styles.waHeaderImg}>
                    {headerUrl || defaultHeaderUrl ? (
                      <img src={headerUrl ?? defaultHeaderUrl ?? undefined} alt="" />
                    ) : (
                      <span>Header image</span>
                    )}
                  </span>
                )}
                <span>{previewText}</span>
              </div>
            </div>
          </div>
        )}

        <div className={styles.modeRow}>
          <label className={styles.radio}>
            <input type="radio" checked={mode === "now"} onChange={() => setMode("now")} />
            Send now
          </label>
          <label className={styles.radio}>
            <input
              type="radio"
              checked={mode === "schedule"}
              onChange={() => setMode("schedule")}
            />
            Schedule
          </label>
          {mode === "schedule" && (
            <input
              type="datetime-local"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
            />
          )}
        </div>

        <footer className={styles.modalFoot}>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={busy || create.isPending || send.isPending}
            onClick={submit}
          >
            {busy ? "Working…" : mode === "now" ? "Send broadcast" : "Schedule"}
          </button>
        </footer>
      </div>
    </div>
  );
}
